import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import StreakModal from './StreakModal';
import ProgressRing from './ProgressRing';
import useStreak from '../hooks/useStreak';
import { deriveStreakState } from '../utils/streak';
import { DUR, EASE_POP } from '../utils/motion';
import { RED, YELLOW, DARK_TEXT } from '../utils/brandColors';

/**
 * The navbar streak pill. Replaces StreakWidget, which could only say "you
 * have N days" — this expresses the four states that actually matter:
 * on-track, done-for-today, about-to-break, and in-recovery.
 */
export default function StreakPill() {
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { streak, refresh } = useStreak();
  const [modalOpen, setModalOpen] = useState(false);
  const [ticking, setTicking] = useState(false);
  const previousStreak = useRef(null);

  const count = streak?.current_streak ?? 0;
  const dailyGoal = streak?.daily_vote_goal ?? 3;
  // Clamped: the raw count can exceed the goal, and the ring encodes progress
  // toward it — an unclamped value would overfill the arc.
  const dailyCount = Math.min(streak?.daily_vote_count ?? 0, dailyGoal);
  const state = deriveStreakState(streak);

  // Fire the tick animation when the streak actually increases — not on first
  // load, where previousStreak is still null.
  useEffect(() => {
    if (previousStreak.current !== null && count > previousStreak.current) {
      setTicking(true);
      const timer = setTimeout(() => setTicking(false), DUR.reveal);
      previousStreak.current = count;
      return () => clearTimeout(timer);
    }
    previousStreak.current = count;
    return undefined;
  }, [count]);

  const BG = {
    active: 'transparent',
    safe: alpha(RED, 0.1),
    at_risk: YELLOW,
    recovery: RED,
  };
  const BORDER = {
    active: theme.palette.divider,
    safe: alpha(RED, 0.35),
    at_risk: YELLOW,
    recovery: RED,
  };
  const NUMERAL_COLOR = {
    active: theme.palette.text.primary,
    safe: theme.palette.text.primary,
    at_risk: DARK_TEXT,
    recovery: '#fff',
  };
  const RING_COLOR = {
    active: RED,
    safe: RED,
    at_risk: YELLOW,
    recovery: '#fff',
  };
  const RING_TRACK = {
    active: theme.palette.divider,
    safe: alpha(RED, 0.25),
    at_risk: alpha(DARK_TEXT, 0.25),
    recovery: 'rgba(255,255,255,0.35)',
  };

  const tooltip = {
    active: `Vote ${Math.max(0, dailyGoal - dailyCount)} more time${dailyGoal - dailyCount === 1 ? '' : 's'} today to keep your streak.`,
    safe: `Today's goal is done. Your ${count}-day streak is safe.`,
    at_risk: `Vote ${dailyGoal} times today to keep your ${count}-day streak.`,
    recovery: 'Recovery quest active — vote to win your streak back.',
  }[state];

  // The recovery pill shows a full ring: the daily goal is not what is being
  // tracked in that state, the quest is.
  const ringValue = state === 'recovery' ? 1 : dailyCount;
  const ringGoal = state === 'recovery' ? 1 : dailyGoal;

  return (
    <>
      <Tooltip title={tooltip}>
        <ButtonBase
          onClick={() => setModalOpen(true)}
          aria-label={`View streak status. ${count} day streak, ${dailyCount} of ${dailyGoal} votes today.`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            height: 32,
            pl: 0.5,
            pr: 1.25,
            borderRadius: '999px',
            border: '1px solid',
            borderColor: BORDER[state],
            bgcolor: BG[state],
            transition: `background-color ${DUR.ui}ms ease, border-color ${DUR.ui}ms ease`,
            '&:active': { transform: 'scale(0.96)' },
            '@keyframes streakTick': {
              '0%': { transform: 'scale(1)' },
              '40%': { transform: 'scale(1.18)' },
              '100%': { transform: 'scale(1)' },
            },
            animation: ticking && !prefersReducedMotion ? `streakTick ${DUR.reveal}ms ${EASE_POP}` : 'none',
          }}
        >
          <ProgressRing
            value={ringValue}
            goal={ringGoal}
            size={24}
            strokeWidth={2.5}
            color={RING_COLOR[state]}
            trackColor={RING_TRACK[state]}
            reducedMotion={prefersReducedMotion}
          />
          <Typography
            component="span"
            sx={{
              fontSize: 15,
              fontWeight: 900,
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
              color: NUMERAL_COLOR[state],
            }}
          >
            {count}
          </Typography>
        </ButtonBase>
      </Tooltip>

      <StreakModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        streakData={streak}
        onStreakChange={refresh}
      />
    </>
  );
}
